/**
 * Admin — seuils de rentabilité (ROI) par geste de travaux.
 *
 * Un seuil (en années) par geste recommandable, + une valeur « Défaut » qui
 * s'applique à tout geste non configuré. Au-delà du seuil, un geste est
 * considéré « non rentable » et filtré des recommandations. Chaque modification
 * est auditée côté serveur.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import {
  getRoiThresholds,
  setRoiThreshold,
  type RoiThresholdItem,
} from "@/api/admin";

const CATEGORY_LABELS: Record<string, string> = {
  isolation: "Isolation",
  menuiseries: "Menuiseries",
  chauffage: "Chauffage",
  ecs: "Eau chaude sanitaire",
  ventilation: "Ventilation",
  renouvelable: "Énergies renouvelables",
};

export default function AdminRoiPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "roi-thresholds"],
    queryFn: getRoiThresholds,
  });

  const mut = useMutation({
    mutationFn: ({ key, years }: { key: string; years: number }) =>
      setRoiThreshold(key, years),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "roi-thresholds"] }),
  });

  const groups: Record<string, RoiThresholdItem[]> = {};
  for (const it of data?.items ?? []) {
    (groups[it.category] ??= []).push(it);
  }

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Link to="/admin" className="text-sm text-blue-900 underline">
          ← Admin
        </Link>
        <h1 className="mt-3 text-2xl font-bold">Seuils de rentabilité (ROI)</h1>
        <p className="mt-1 text-sm text-gray-600">
          En années. Un geste dont le retour sur investissement dépasse son seuil
          est considéré « non rentable » et écarté des recommandations.
        </p>

        {isLoading && <p className="mt-6 text-sm text-gray-500">Chargement…</p>}
        {error && (
          <p className="mt-6 text-sm text-red-700">Impossible de charger les seuils.</p>
        )}

        {data && (
          <>
            <section className="mt-6 rounded-lg border-2 border-blue-200 bg-blue-50 p-4">
              <RoiRow
                label="Défaut (tout geste non configuré)"
                years={data.default_years}
                busy={mut.isPending}
                onSave={(years) => mut.mutate({ key: "default", years })}
              />
            </section>

            {Object.entries(groups).map(([cat, items]) => (
              <section key={cat} className="mt-6">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  {CATEGORY_LABELS[cat] ?? cat}
                </h2>
                <div className="mt-2 space-y-2">
                  {items.map((it) => (
                    <div
                      key={it.key}
                      className="rounded-lg border border-gray-200 bg-white p-3"
                    >
                      <RoiRow
                        label={it.label}
                        years={it.years}
                        configured={it.configured}
                        busy={mut.isPending}
                        onSave={(years) => mut.mutate({ key: it.key, years })}
                      />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </div>
    </Layout>
  );
}

function RoiRow({
  label,
  years,
  configured,
  busy,
  onSave,
}: {
  label: string;
  years: number;
  configured?: boolean;
  busy: boolean;
  onSave: (years: number) => void;
}) {
  const [value, setValue] = useState<string>(String(years));
  const parsed = Number(value);
  const dirty = Number.isFinite(parsed) && parsed >= 1 && parsed !== years;

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-sm">
        {label}
        {configured === false && (
          <span className="ml-2 text-xs text-gray-400">(suit le défaut)</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={1}
          max={100}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
        />
        <span className="text-xs text-gray-500">ans</span>
        <button
          type="button"
          disabled={busy || !dirty}
          onClick={() => onSave(parsed)}
          className="rounded bg-blue-900 px-3 py-1 text-sm text-white hover:bg-blue-800 disabled:opacity-40"
        >
          Enregistrer
        </button>
      </div>
    </div>
  );
}
